<template>
  <v-dialog :model-value="modelValue" max-width="640" @update:model-value="handleToggle">
    <v-card>
      <v-card-title class="text-h6">
        {{ $t("ncrm.integrations.appSyncs.candidates.boardDialog.title") }}
      </v-card-title>
      <v-card-text class="pt-2">
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{
            $t("ncrm.integrations.appSyncs.candidates.boardDialog.subtitle", {
              count: candidatesCount,
            })
          }}
        </div>

        <v-select
          v-model="selectedBoardId"
          :items="boardOptions"
          :label="$t('ncrm.integrations.appSyncs.candidates.boardDialog.board')"
          hide-details
          :loading="boardsLoading"
          class="mb-4"
        />

        <v-select
          v-model="selectedStageId"
          :items="stageOptions"
          :label="$t('ncrm.integrations.appSyncs.candidates.boardDialog.stage')"
          hide-details
          :loading="stagesLoading"
          :disabled="!selectedBoardId"
        />

        <div v-if="errorMessage" class="text-body-2 text-error mt-4">
          {{ errorMessage }}
        </div>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-btn variant="text" @click="closeDialog">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          :disabled="!selectedBoardId || !selectedStageId"
          @click="submitSelection"
        >
          {{ $t("ncrm.integrations.appSyncs.candidates.boardDialog.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "NcrmMitpLeadBoardDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    candidatesCount: {
      type: Number,
      default: 0,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["confirm", "error", "update:modelValue"],
  data() {
    return {
      boardsLoading: false,
      boards: [],
      errorMessage: "",
      selectedBoardId: null,
      selectedStageId: null,
      stages: [],
      stagesLoading: false,
    };
  },
  computed: {
    boardOptions() {
      return this.boards.map((board) => ({
        title: board?.name || `#${board?.id || "-"}`,
        value: Number(board?.id || 0),
      }));
    },
    stageOptions() {
      return this.stages.map((stage) => ({
        title: stage?.name || `#${stage?.id || "-"}`,
        value: Number(stage?.id || 0),
      }));
    },
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.openDialog();
        return;
      }
      this.resetState();
    },
    selectedBoardId(value, previousValue) {
      if (value === previousValue) return;
      this.selectedStageId = null;
      this.stages = [];
      if (!value) return;
      this.fetchStages();
    },
  },
  methods: {
    handleToggle(value) {
      this.$emit("update:modelValue", Boolean(value));
    },
    openDialog() {
      this.errorMessage = "";
      this.fetchBoards();
    },
    closeDialog() {
      this.$emit("update:modelValue", false);
    },
    resetState() {
      this.boardsLoading = false;
      this.errorMessage = "";
      this.selectedBoardId = null;
      this.selectedStageId = null;
      this.stages = [];
      this.stagesLoading = false;
    },
    async fetchBoards() {
      if (this.boardsLoading) return;
      this.boardsLoading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get("/ncrm/integrations/mitp/lead-boards");
        this.boards = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.boards = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.candidates.messages.loadBoardsError");
        this.$emit("error", this.errorMessage);
      } finally {
        this.boardsLoading = false;
      }
    },
    async fetchStages() {
      if (!this.selectedBoardId || this.stagesLoading) return;
      this.stagesLoading = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.get(`/ncrm/integrations/mitp/lead-boards/${this.selectedBoardId}/stages`);
        this.stages = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        this.stages = [];
        this.errorMessage =
          error?.response?.data?.error || this.$t("ncrm.integrations.appSyncs.candidates.messages.loadStagesError");
        this.$emit("error", this.errorMessage);
      } finally {
        this.stagesLoading = false;
      }
    },
    submitSelection() {
      if (!this.selectedBoardId || !this.selectedStageId) return;
      this.$emit("confirm", {
        boardId: this.selectedBoardId,
        stageId: this.selectedStageId,
      });
    },
  },
};
</script>
